import { useState } from "react";
import { Volume2, MessageCircle, Filter } from "lucide-react";
import phrasesData from "../data/phrases.json";

function speak(text) {
  if ("speechSynthesis" in window) {
    const u = new SpeechSynthesisUtterance(text);
    u.rate = 0.8;
    u.lang = "en-US";
    window.speechSynthesis.speak(u);
  }
}

export default function Phrases() {
  const [activeCategory, setActiveCategory] = useState("all");
  const [formalFilter, setFormalFilter] = useState("all");

  const filtered = activeCategory === "all"
    ? phrasesData
    : phrasesData.filter((c) => c.category === activeCategory);

  return (
    <div className="space-y-6 page-enter relative">
      <div className="blob blob-pink" style={{ width: 320, height: 320, top: -40, right: -40, opacity: 0.18 }} />
      <div className="blob blob-cyan" style={{ width: 280, height: 280, top: 280, left: -40, opacity: 0.15 }} />
      <div className="relative">
        <h1 className="font-display text-4xl md:text-5xl font-normal mb-2"><em>Phrases.</em></h1>
        <p className="text-gray-600 dark:text-gray-300 font-semibold">Essential expressions for everyday English communication.</p>
      </div>

      {/* Category Filter */}
      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => setActiveCategory("all")}
          className={`px-4 py-2 rounded-xl text-sm font-semibold transition-all ${
            activeCategory === "all" ? "bg-ink-900 text-white shadow-md" : "bg-white text-gray-600 hover:bg-gray-100 border border-gray-200"
          }`}
        >
          All Categories
        </button>
        {phrasesData.map((cat) => (
          <button
            key={cat.category}
            onClick={() => setActiveCategory(cat.category)}
            className={`px-4 py-2 rounded-xl text-sm font-semibold transition-all ${
              activeCategory === cat.category ? "bg-ink-900 text-white shadow-md" : "bg-white text-gray-600 hover:bg-gray-100 border border-gray-200"
            }`}
          >
            {cat.category}
          </button>
        ))}
      </div>

      {/* Formal/Informal Filter */}
      <div className="flex items-center gap-3">
        <Filter className="w-4 h-4 text-gray-400" />
        <div className="flex gap-2">
          {["all", "formal", "informal"].map((f) => (
            <button
              key={f}
              onClick={() => setFormalFilter(f)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                formalFilter === f ? "bg-ink-800 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
            >
              {f === "all" ? "All" : f === "formal" ? "Formal" : "Informal"}
            </button>
          ))}
        </div>
      </div>

      {/* Phrases List */}
      <div className="space-y-6">
        {filtered.map((cat) => {
          const phrases = formalFilter === "all"
            ? cat.phrases
            : cat.phrases.filter((p) => formalFilter === "formal" ? p.formal : !p.formal);

          if (phrases.length === 0) return null;

          return (
            <div key={cat.category} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <div className="flex items-center gap-2 mb-4">
                <MessageCircle className="w-5 h-5 text-gold-700" />
                <h2 className="text-lg font-bold text-gray-800">{cat.category}</h2>
                <span className="text-xs text-gray-400 ml-auto">{phrases.length} phrases</span>
              </div>
              <div className="space-y-3">
                {phrases.map((p, i) => (
                  <div key={i} className="flex items-start gap-3 bg-gray-50 rounded-xl p-4">
                    <button
                      onClick={() => speak(p.phrase)}
                      className="p-2 bg-cream-100 rounded-full text-gold-700 hover:bg-cream-200 transition-all flex-shrink-0 mt-0.5"
                    >
                      <Volume2 className="w-4 h-4" />
                    </button>
                    <div className="flex-1">
                      <p className="font-medium text-gray-800">{p.phrase}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs text-gray-500">{p.use}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${
                          p.formal ? "bg-sky-light text-sky-dark" : "bg-sage-light text-sage-dark"
                        }`}>
                          {p.formal ? "Formal" : "Informal"}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
